import React, { useState } from 'react';
import { useQuery, useMutation } from '@apollo/client';
import {
  Box, Typography, Grid, Card, CardContent, CardActions,
  Button, Stack, Chip, IconButton, Tooltip, Alert, CircularProgress,
  Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, MenuItem, Switch, FormControlLabel,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import SaveIcon from '@mui/icons-material/Save';
import PaletteIcon from '@mui/icons-material/Palette';
import {
  DECORATION_THEMES_QUERY,
  CREATE_DECORATION_THEME_MUTATION,
  UPDATE_DECORATION_THEME_MUTATION,
  DELETE_DECORATION_THEME_MUTATION,
} from '../graphql/queries';
import { useLov } from '../hooks/useLov';
import ContractedVendorsSection from '../components/ContractedVendorsSection';

import Paged from '../components/Paged'; // lists beyond 10 are paged
const EMPTY = { name: '', style: '', colorPalette: [], description: '', estimatedCost: '', imageUrl: '', isActive: true };

export default function AdminDecorationThemesPage() {
  const [dialog, setDialog]   = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm]       = useState(EMPTY);
  const [newColor, setNewColor] = useState('');
  const [snack, setSnack]     = useState({ open: false, msg: '', ok: true });

  const { data, loading, refetch } = useQuery(DECORATION_THEMES_QUERY, { variables: {}, fetchPolicy: 'cache-and-network' });

  const { options: styleOpts } = useLov('decoration_style');

  const toast = (msg, ok = true) => setSnack({ open: true, msg, ok });

  const [create, { loading: creating }] = useMutation(CREATE_DECORATION_THEME_MUTATION, {
    onCompleted: () => { closeDialog(); refetch(); toast('Theme created.'); },
    onError: e => toast(e.message, false),
  });
  const [update, { loading: updating }] = useMutation(UPDATE_DECORATION_THEME_MUTATION, {
    onCompleted: () => { closeDialog(); refetch(); toast('Theme updated.'); },
    onError: e => toast(e.message, false),
  });
  const [remove] = useMutation(DELETE_DECORATION_THEME_MUTATION, {
    onCompleted: () => { refetch(); toast('Theme deleted.'); },
    onError: e => toast(e.message, false),
  });

  const themes = data?.decorationThemes || [];

  const openCreate = () => { setEditing(null); setForm(EMPTY); setDialog(true); };
  const openEdit   = (t) => {
    setEditing(t);
    setForm({ name: t.name, style: t.style || '', colorPalette: t.colorPalette || [], description: t.description || '', estimatedCost: t.estimatedCost != null ? String(t.estimatedCost) : '', imageUrl: t.imageUrl || '', isActive: t.isActive });
    setDialog(true);
  };
  const closeDialog = () => { setDialog(false); setEditing(null); setForm(EMPTY); setNewColor(''); };

  const addColor = () => {
    if (!newColor.trim()) return;
    setForm(f => ({ ...f, colorPalette: [...(f.colorPalette || []), newColor.trim()] }));
    setNewColor('');
  };
  const removeColor = (idx) => setForm(f => ({ ...f, colorPalette: f.colorPalette.filter((_, i) => i !== idx) }));

  const handleSave = () => {
    if (!form.name.trim()) return;
    const vars = { ...form, estimatedCost: form.estimatedCost ? parseFloat(form.estimatedCost) : null };
    if (editing) update({ variables: { id: editing.id, ...vars } });
    else create({ variables: vars });
  };

  return (
    <Box p={3}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
        <Box>
          <Typography variant="h4" fontWeight={700}>Decoration Themes</Typography>
          <Typography variant="body2" color="text.secondary">Manage available decoration themes for wedding ceremonies</Typography>
        </Box>
        <Button variant="contained" startIcon={<AddIcon />} onClick={openCreate}>New Theme</Button>
      </Stack>

      <ContractedVendorsSection category="decoration" />

      {snack.open && (
        <Alert severity={snack.ok ? 'success' : 'error'} onClose={() => setSnack(s => ({ ...s, open: false }))} sx={{ mb: 2 }}>
          {snack.msg}
        </Alert>
      )}

      {loading ? <CircularProgress /> : (
        <Grid container spacing={2}>
          <Paged items={themes}>{rows => rows.map(t => (
            <Grid item xs={12} sm={6} md={4} key={t.id}>
              <Card variant="outlined" sx={{ opacity: t.isActive ? 1 : 0.6 }}>
                <CardContent>
                  <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                    <Box>
                      <Typography variant="h6" fontWeight={600}>{t.name}</Typography>
                      <Chip label={t.style || 'General'} size="small" color="primary" sx={{ mt: 0.5 }} />
                    </Box>
                    <Chip label={t.isActive ? 'Active' : 'Inactive'} size="small" color={t.isActive ? 'success' : 'default'} />
                  </Stack>
                  {t.description && <Typography variant="body2" color="text.secondary" mt={1}>{t.description}</Typography>}
                  {t.estimatedCost != null && (
                    <Typography variant="body2" mt={1}>Est. Cost: <strong>₹{t.estimatedCost.toLocaleString()}</strong></Typography>
                  )}
                  {(t.colorPalette || []).length > 0 && (
                    <Stack direction="row" spacing={0.5} mt={1} flexWrap="wrap">
                      {t.colorPalette.map((c, i) => (
                        <Chip key={i} label={c} size="small" icon={<PaletteIcon />} />
                      ))}
                    </Stack>
                  )}
                </CardContent>
                <CardActions sx={{ justifyContent: 'flex-end' }}>
                  <Tooltip title="Edit"><IconButton size="small" onClick={() => openEdit(t)}><EditIcon fontSize="small" /></IconButton></Tooltip>
                  <Tooltip title="Delete"><IconButton size="small" color="error" onClick={() => remove({ variables: { id: t.id } })}><DeleteIcon fontSize="small" /></IconButton></Tooltip>
                </CardActions>
              </Card>
            </Grid>
          ))}</Paged>
          {themes.length === 0 && (
            <Grid item xs={12}><Alert severity="info">No decoration themes found. Create the first one.</Alert></Grid>
          )}
        </Grid>
      )}

      <Dialog open={dialog} onClose={closeDialog} maxWidth="sm" fullWidth>
        <DialogTitle>{editing ? 'Edit Decoration Theme' : 'New Decoration Theme'}</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2} mt={1}>
            <TextField label="Name" value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} required fullWidth />
            <TextField select label="Style" value={form.style} onChange={e => setForm(f => ({ ...f, style: e.target.value }))} fullWidth>
              <MenuItem value="">— General —</MenuItem>
              {styleOpts.map(s => <MenuItem key={s.value} value={s.value}>{s.label}</MenuItem>)}
            </TextField>
            <TextField label="Description" value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} multiline rows={3} fullWidth />
            <TextField label="Estimated Cost (₹)" value={form.estimatedCost} onChange={e => setForm(f => ({ ...f, estimatedCost: e.target.value }))} type="number" fullWidth />
            <TextField label="Image URL" value={form.imageUrl} onChange={e => setForm(f => ({ ...f, imageUrl: e.target.value }))} fullWidth />
            <FormControlLabel
              control={<Switch checked={form.isActive} onChange={e => setForm(f => ({ ...f, isActive: e.target.checked }))} />}
              label="Active"
            />
            <Typography variant="subtitle2">Color Palette</Typography>
            <Stack direction="row" spacing={0.5} flexWrap="wrap">
              {(form.colorPalette || []).map((c, i) => (
                <Chip key={i} label={c} size="small" onDelete={() => removeColor(i)} />
              ))}
            </Stack>
            <Stack direction="row" spacing={1}>
              <TextField label="Add color" value={newColor} onChange={e => setNewColor(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && addColor()} size="small" fullWidth />
              <Button variant="outlined" onClick={addColor}>Add</Button>
            </Stack>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDialog}>Cancel</Button>
          <Button variant="contained" onClick={handleSave} disabled={creating || updating} startIcon={<SaveIcon />}>
            {editing ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
